import { useTheme } from 'next-themes';
import { createContext, useContext, useEffect, useRef, useState, useCallback, type ReactNode } from 'react';
import { fetchCurrentUserApi, updateUserApi, setAuthHeader } from '@/apis/auth.api';
import i18n from '@/i18n';
import { cachedSessionUser } from '@/lib/session';
import AuthService from '@/services/auth.service';
import { initHTTPHeader } from '@/services/http.service';
import type { ICurrentUser, IUserLogin, IRegisterUser, ITwoFactorLoginResult } from '@/types/auth';

const SUPPORTED_LANGS = ['en', 'fr'] as const;
const SUPPORTED_THEMES = ['light', 'dark', 'system'] as const;

const applyServerPreferences = (user: ICurrentUser | null, setTheme: (theme: string) => void) => {
  if (!user) return;
  if (user.lang && (SUPPORTED_LANGS as readonly string[]).includes(user.lang)) {
    if (i18n.language !== user.lang) {
      void i18n.changeLanguage(user.lang);
    }
    localStorage.setItem('i18n_lang', user.lang);
  }
  if (user.theme && (SUPPORTED_THEMES as readonly string[]).includes(user.theme)) {
    setTheme(user.theme);
  }
};

interface AuthContextValue {
  user: ReturnType<typeof AuthService.getUser>;
  currentUser: ICurrentUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  pendingTwoFactor: ITwoFactorLoginResult | null;
  login: (user: IUserLogin) => Promise<{ twoFactorRequired: true } | void>;
  loginWithToken: (token: string) => Promise<void>;
  verifyOtp: (otpCode: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (user: IRegisterUser) => Promise<void>;
  refreshCurrentUser: () => Promise<void>;
  updateUser: (id: number, data: Record<string, unknown>) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState(AuthService.getUser());
  // The stored user stands in until the server confirms the session, but only
  // when the token was issued to that user and has not expired (lib/session):
  // protected pages then draw at once instead of behind a spinner, and
  // nothing of another account can show.
  const [currentUser, setCurrentUser] = useState<ICurrentUser | null>(() =>
    cachedSessionUser(AuthService.getToken(), AuthService.getUser()),
  );
  const [isLoading, setIsLoading] = useState(!!AuthService.getToken());
  const [pendingTwoFactor, setPendingTwoFactor] = useState<ITwoFactorLoginResult | null>(null);
  const { setTheme } = useTheme();
  // The token this tab is running on (see the storage listener below).
  const sessionToken = useRef(AuthService.getToken());

  const loadCurrentUser = useCallback(async () => {
    try {
      const res = await fetchCurrentUserApi();
      const resData = res.data as { user?: ICurrentUser } | ICurrentUser;
      const loaded = (resData as { user?: ICurrentUser }).user ?? (resData as ICurrentUser);
      setCurrentUser(loaded);
      // The next visit starts from this copy: a changed role, apps or
      // language never outlives one confirmation.
      localStorage.setItem('user', JSON.stringify(loaded));
      setUser(loaded);
      applyServerPreferences(loaded, setTheme);
    } catch {
      // token invalid — auth service interceptor handles redirect
    } finally {
      setIsLoading(false);
    }
  }, [setTheme]);

  // Another tab signed out, or signed in as someone else: this one must not
  // go on showing the old account. Signed out → the login page; a different
  // token → start over, so every cache and channel belongs to the new one.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.storageArea !== localStorage || (event.key !== 'token' && event.key !== null)) return;
      const token = AuthService.getToken();
      if (token === sessionToken.current) return;
      if (!token) window.location.href = '/login';
      else window.location.reload();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    initHTTPHeader();
    if (AuthService.getToken()) {
      void loadCurrentUser();
    } else {
      setIsLoading(false);
    }
  }, [loadCurrentUser]);

  const login = async (credentials: IUserLogin): Promise<{ twoFactorRequired: true } | void> => {
    const result = await AuthService.login(credentials);
    if (result && 'twoFactorRequired' in result) {
      setPendingTwoFactor(result);
      return { twoFactorRequired: true };
    }
    sessionToken.current = AuthService.getToken();
    setUser(result);
    await loadCurrentUser();
  };

  const verifyOtp = async (otpCode: string) => {
    if (!pendingTwoFactor) throw new Error('No pending two-factor session');
    const data = await AuthService.loginWithOtp(pendingTwoFactor.preAuthToken, otpCode);
    setPendingTwoFactor(null);
    sessionToken.current = AuthService.getToken();
    setUser(data);
    await loadCurrentUser();
  };

  const logout = async () => {
    await AuthService.logout();
    sessionToken.current = null;
    setUser(null);
    setCurrentUser(null);
    setPendingTwoFactor(null);
  };

  const loginWithToken = async (token: string) => {
    localStorage.setItem('token', token);
    sessionToken.current = token;
    setAuthHeader(token);
    try {
      const res = await fetchCurrentUserApi();
      const resData = res.data as { user?: ICurrentUser } | ICurrentUser;
      const loaded = (resData as { user?: ICurrentUser }).user ?? (resData as ICurrentUser);
      localStorage.setItem('user', JSON.stringify(loaded));
      setUser(loaded);
      setCurrentUser(loaded);
      applyServerPreferences(loaded, setTheme);
    } catch {
      // token invalid
    }
  };

  const register = async (data: IRegisterUser) => {
    await AuthService.register(data);
  };

  const updateUser = async (id: number, data: Record<string, unknown>) => {
    const res = await updateUserApi(id, data);
    const resData = res.data as { user?: ICurrentUser } | ICurrentUser;
    const updated = (resData as { user?: ICurrentUser }).user ?? (resData as ICurrentUser);
    setCurrentUser(updated);
    localStorage.setItem('user', JSON.stringify(updated));
    applyServerPreferences(updated, setTheme);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        currentUser,
        isAuthenticated: !!user,
        isLoading,
        pendingTwoFactor,
        login,
        loginWithToken,
        verifyOtp,
        logout,
        register,
        refreshCurrentUser: loadCurrentUser,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/** The session when an AuthProvider is above, else null (the public form's own bundle has none). */
export function useOptionalAuth() {
  return useContext(AuthContext);
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
